interface MultipartUploadOptions {
  onProgress?: (progress: number) => void;
  partSize?: number; // in bytes, override adaptive sizing
}

export type UploadTarget =
  | { type: "booking"; bookingId: number }
  | { type: "private-recording"; recordingId: number };

function getTargetId(target: UploadTarget): number {
  return target.type === "booking" ? target.bookingId : target.recordingId;
}

function getBaseUrl(target: UploadTarget): string {
  return target.type === "booking"
    ? `/api/bookings/${target.bookingId}`
    : `/api/admin/private-recordings/${target.recordingId}`;
}

interface UploadResumeState {
  targetKey: string;
  uploadId: string;
  key: string;
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  partSize: number;
  totalParts: number;
  completedParts: Array<{ partNumber: number; etag: string }>;
  setlist: string;
  createdAt: number;
}

const STORAGE_PREFIX = "chfm_upload_";
const MAX_RESUME_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const CONCURRENCY = 3;
const MAX_RETRIES = 3;
const MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB (R2 minimum)
const MAX_PART_SIZE = 500 * 1024 * 1024; // 500MB
const TARGET_PARTS = 1000;

function calculatePartSize(fileSize: number): number {
  const calculated = Math.ceil(fileSize / TARGET_PARTS);
  return Math.max(MIN_PART_SIZE, Math.min(MAX_PART_SIZE, calculated));
}

function getStorageKey(target: UploadTarget): string {
  const suffix = target.type === "booking" ? `booking_${target.bookingId}` : `rec_${target.recordingId}`;
  return `${STORAGE_PREFIX}${suffix}`;
}

function loadResumeState(target: UploadTarget): UploadResumeState | null {
  try {
    const raw = localStorage.getItem(getStorageKey(target));
    if (!raw) return null;
    const state: UploadResumeState = JSON.parse(raw);
    // Expire old states
    if (Date.now() - state.createdAt > MAX_RESUME_AGE_MS) {
      localStorage.removeItem(getStorageKey(target));
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

function saveResumeState(target: UploadTarget, state: UploadResumeState): void {
  try {
    localStorage.setItem(getStorageKey(target), JSON.stringify(state));
  } catch {
    // localStorage full or unavailable — non-fatal
  }
}

function clearResumeState(target: UploadTarget): void {
  try {
    localStorage.removeItem(getStorageKey(target));
  } catch {
    // non-fatal
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class MultipartUploader {
  private activeUploads = new Map<
    string,
    { xhrs: Set<XMLHttpRequest>; abortController?: AbortController; uploadId?: string; key?: string }
  >();

  private targetKey(target: UploadTarget): string {
    return target.type === "booking" ? `booking_${target.bookingId}` : `rec_${target.recordingId}`;
  }

  async uploadFile(
    file: File,
    target: UploadTarget,
    setlist: string,
    options: MultipartUploadOptions = {}
  ): Promise<any> {
    const { onProgress = () => {} } = options;
    const partSize = options.partSize ?? calculatePartSize(file.size);
    const tk = this.targetKey(target);

    try {
      // If file is small enough, use single upload
      if (file.size <= partSize) {
        return await this.singleUpload(file, target, setlist, onProgress);
      }

      // Otherwise, use multipart upload
      return await this.multipartUpload(file, target, setlist, partSize, onProgress);
    } finally {
      this.activeUploads.delete(tk);
    }
  }

  async cancelUpload(target: UploadTarget): Promise<void> {
    const tk = this.targetKey(target);
    const upload = this.activeUploads.get(tk);
    if (!upload) return;

    // Cancel all active XHRs
    Array.from(upload.xhrs).forEach((xhr) => xhr.abort());

    // Cancel fetch requests
    if (upload.abortController) {
      upload.abortController.abort();
    }

    // Abort multipart upload on server
    if (upload.uploadId && upload.key) {
      try {
        await fetch(`${getBaseUrl(target)}/multipart/abort`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uploadId: upload.uploadId, key: upload.key }),
        });
      } catch (error) {
        console.error("Error aborting multipart upload:", error);
      }
    }

    clearResumeState(target);
    this.activeUploads.delete(tk);
  }

  private async singleUpload(
    file: File,
    target: UploadTarget,
    setlist: string,
    onProgress: (progress: number) => void
  ): Promise<any> {
    const baseUrl = getBaseUrl(target);
    const tk = this.targetKey(target);

    const presignedResponse = await fetch(`${baseUrl}/presigned-upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
      }),
    });

    if (!presignedResponse.ok) {
      const errorData = await presignedResponse.json().catch(() => ({ message: "Failed to get upload URL" }));
      throw new Error(errorData.message || `Failed to get upload URL: ${presignedResponse.statusText}`);
    }

    const { uploadUrl, key } = await presignedResponse.json();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.activeUploads.set(tk, { xhrs: new Set([xhr]), key });

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const confirmResponse = await fetch(`${baseUrl}/confirm-upload`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ key, setlist, fileSize: file.size }),
            });
            if (!confirmResponse.ok) throw new Error("Failed to confirm upload");
            resolve(await confirmResponse.json());
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error(`Failed to upload to R2: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
      xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")));

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  }

  private async multipartUpload(
    file: File,
    target: UploadTarget,
    setlist: string,
    partSize: number,
    onProgress: (progress: number) => void
  ): Promise<any> {
    const baseUrl = getBaseUrl(target);
    const tk = this.targetKey(target);
    const totalParts = Math.ceil(file.size / partSize);
    let uploadId: string;
    let key: string;
    let completedParts: Array<{ partNumber: number; etag: string }> = [];

    // Try to resume from localStorage
    const resumeState = loadResumeState(target);
    let resumed = false;

    if (
      resumeState &&
      resumeState.fileName === file.name &&
      resumeState.fileSize === file.size &&
      resumeState.fileLastModified === file.lastModified &&
      resumeState.partSize === partSize
    ) {
      // Validate with server that the upload still exists
      try {
        const listResponse = await fetch(`${baseUrl}/multipart/list-parts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uploadId: resumeState.uploadId, key: resumeState.key }),
        });

        if (listResponse.ok) {
          const { parts: serverParts } = await listResponse.json();
          // Build a set of confirmed part numbers from the server
          const confirmedParts = new Map<number, string>();
          for (const p of serverParts) {
            confirmedParts.set(p.partNumber, p.etag);
          }

          // Only keep parts that the server confirms
          completedParts = resumeState.completedParts.filter(
            (p) => confirmedParts.has(p.partNumber)
          );

          uploadId = resumeState.uploadId;
          key = resumeState.key;
          resumed = true;
          console.log(
            `[Upload] Resuming upload: ${completedParts.length}/${totalParts} parts already uploaded`
          );
        } else {
          // Upload expired or invalid — start fresh
          clearResumeState(target);
        }
      } catch {
        clearResumeState(target);
      }
    } else if (resumeState) {
      // File identity doesn't match — discard old state
      clearResumeState(target);
    }

    // If not resumed, initialize a new multipart upload
    if (!resumed) {
      const initResponse = await fetch(`${baseUrl}/multipart/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          totalParts,
        }),
      });

      if (!initResponse.ok) {
        throw new Error("Failed to initialize multipart upload");
      }

      const initData = await initResponse.json();
      uploadId = initData.uploadId;
      key = initData.key;
    }

    // Save initial resume state
    const resumeData: UploadResumeState = {
      targetKey: tk,
      uploadId: uploadId!,
      key: key!,
      fileName: file.name,
      fileSize: file.size,
      fileLastModified: file.lastModified,
      partSize,
      totalParts,
      completedParts,
      setlist,
      createdAt: resumeState?.createdAt ?? Date.now(),
    };
    saveResumeState(target, resumeData);

    // Track this multipart upload
    const abortController = new AbortController();
    const xhrs = new Set<XMLHttpRequest>();
    this.activeUploads.set(tk, {
      xhrs,
      abortController,
      uploadId: uploadId!,
      key: key!,
    });

    // Build list of parts that still need uploading
    const completedSet = new Set(completedParts.map((p) => p.partNumber));
    const pendingPartNumbers: number[] = [];
    for (let i = 1; i <= totalParts; i++) {
      if (!completedSet.has(i)) {
        pendingPartNumbers.push(i);
      }
    }

    // Per-part byte counters for smooth progress
    const partBytesLoaded = new Map<number, number>();
    // Pre-fill completed parts
    for (const p of completedParts) {
      const start = (p.partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      partBytesLoaded.set(p.partNumber, end - start);
    }

    const updateProgress = () => {
      let totalLoaded = 0;
      partBytesLoaded.forEach((bytes) => {
        totalLoaded += bytes;
      });
      onProgress(Math.min(99, Math.round((totalLoaded / file.size) * 100)));
    };

    // Report initial progress for resumed parts
    if (completedParts.length > 0) {
      updateProgress();
    }

    try {
      // Worker pool: process pending parts with CONCURRENCY concurrent uploads
      let nextIndex = 0;
      const errors: Error[] = [];

      const worker = async () => {
        while (nextIndex < pendingPartNumbers.length) {
          if (abortController.signal.aborted) return;

          const idx = nextIndex++;
          if (idx >= pendingPartNumbers.length) return;

          const partNumber = pendingPartNumbers[idx];
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, file.size);
          const partBlob = file.slice(start, end);

          const etag = await this.uploadPartWithRetry(
            partBlob,
            partNumber,
            target,
            uploadId!,
            key!,
            xhrs,
            abortController,
            (loaded) => {
              partBytesLoaded.set(partNumber, loaded);
              updateProgress();
            }
          );

          completedParts.push({ partNumber, etag });
          partBytesLoaded.set(partNumber, partBlob.size);
          updateProgress();

          // Persist to localStorage after each part
          resumeData.completedParts = [...completedParts];
          saveResumeState(target, resumeData);
        }
      };

      // Launch concurrent workers
      const workers: Promise<void>[] = [];
      for (let i = 0; i < CONCURRENCY; i++) {
        workers.push(worker().catch((err) => { errors.push(err); }));
      }
      await Promise.all(workers);

      if (errors.length > 0) {
        // Don't abort on server — preserve for resume
        throw errors[0];
      }

      // Complete the multipart upload
      const sortedParts = completedParts
        .slice()
        .sort((a, b) => a.partNumber - b.partNumber);

      const completeResponse = await fetch(`${baseUrl}/multipart/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uploadId: uploadId!,
          key: key!,
          parts: sortedParts,
          setlist,
          fileSize: file.size,
        }),
      });

      if (!completeResponse.ok) {
        throw new Error("Failed to complete multipart upload");
      }

      onProgress(100);
      clearResumeState(target);
      return await completeResponse.json();
    } catch (error) {
      // Don't abort the server-side upload — preserve state for resume
      // The localStorage state is already saved for next attempt
      throw error;
    }
  }

  private async uploadPartWithRetry(
    partBlob: Blob,
    partNumber: number,
    target: UploadTarget,
    uploadId: string,
    key: string,
    xhrs: Set<XMLHttpRequest>,
    abortController: AbortController,
    onPartProgress: (loaded: number) => void
  ): Promise<string> {
    let lastError: Error | null = null;
    const baseUrl = getBaseUrl(target);

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (abortController.signal.aborted) {
        throw new Error("Upload cancelled");
      }

      try {
        // Get a (fresh) presigned URL for this part
        const partUrlResponse = await fetch(`${baseUrl}/multipart/part-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uploadId, partNumber, key }),
          signal: abortController.signal,
        });

        if (!partUrlResponse.ok) {
          throw new Error(`Failed to get URL for part ${partNumber}`);
        }

        const { uploadUrl } = await partUrlResponse.json();

        const etag = await this.uploadPartXHR(partBlob, uploadUrl, xhrs, onPartProgress);
        return etag;
      } catch (err: any) {
        if (err.message === "Upload cancelled" || abortController.signal.aborted) {
          throw new Error("Upload cancelled");
        }
        lastError = err;
        if (attempt < MAX_RETRIES - 1) {
          const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          console.warn(
            `[Upload] Part ${partNumber} attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
            err.message
          );
          onPartProgress(0); // Reset part progress on retry
          await sleep(delay);
        }
      }
    }

    throw lastError || new Error(`Failed to upload part ${partNumber} after ${MAX_RETRIES} attempts`);
  }

  private uploadPartXHR(
    partBlob: Blob,
    uploadUrl: string,
    xhrs: Set<XMLHttpRequest>,
    onPartProgress: (loaded: number) => void
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrs.add(xhr);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onPartProgress(event.loaded);
        }
      });

      xhr.addEventListener("load", () => {
        xhrs.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag") || "";
          resolve(etag);
        } else {
          reject(new Error(`Part upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        xhrs.delete(xhr);
        reject(new Error("Network error during part upload"));
      });

      xhr.addEventListener("abort", () => {
        xhrs.delete(xhr);
        reject(new Error("Upload cancelled"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.send(partBlob);
    });
  }
}

// Singleton instance
let uploaderInstance: MultipartUploader | null = null;

export function getMultipartUploader(): MultipartUploader {
  if (!uploaderInstance) {
    uploaderInstance = new MultipartUploader();
  }
  return uploaderInstance;
}
